import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import "./App.css";

const API = "https://soft-web-6.onrender.com/";

/* ---------------- APP ---------------- */
function App() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState(
    JSON.parse(localStorage.getItem("cart")) || []
  );
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  return (
    <BrowserRouter>
      <nav className="navbar">
        <h2>SoftSell</h2>
        <Link to="/">Deals</Link>
        {user && <Link to="/dashboard">Dashboard</Link>}

        <button className="cart-btn" onClick={() => setShowCart(true)}>
          🛒 Cart ({cart.length})
        </button>

        {!user ? (
          <Link to="/login">Login</Link>
        ) : (
          <button
            onClick={() => {
              localStorage.removeItem("user");
              setUser(null);
            }}
          >
            Logout
          </button>
        )}
      </nav>

      {/* SEARCH */}
      <div className="searchbar-wrapper">
        <input
          className="searchbar"
          placeholder="Search software deals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Routes>
        <Route
          path="/"
          element={<Deals user={user} search={search} cart={cart} setCart={setCart} />}
        />
        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/dashboard" element={<Dashboard user={user} />} />
      </Routes>

      {showCart && (
        <Cart
          cart={cart}
          setCart={setCart}
          user={user}
          close={() => setShowCart(false)}
        />
      )}
    </BrowserRouter>
  );
}

/* ---------------- DEALS ---------------- */
function Deals({ user, search, cart, setCart }) {
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/deals`)
      .then((r) => r.json())
      .then(setDeals);
  }, []);

  // Search only filters deals
  const filtered = deals.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.description.toLowerCase().includes(search.toLowerCase())
  );

  const addToCart = (deal) => {
    if (cart.find((c) => c.id === deal.id)) return;
    setCart([...cart, deal]);
  };

  return (
    <div className="grid">
      {filtered.map((d) => (
        <div className="card" key={d.id}>
          <h3>{d.name}</h3>
          <p>{d.description}</p>
          <h2>${d.price}</h2>
          <button onClick={() => addToCart(d)}>Add to Cart</button>
        </div>
      ))}
    </div>
  );
}

/* ---------------- CART ---------------- */
function Cart({ cart, setCart, user, close }) {
  const total = cart.reduce((sum, i) => sum + i.price, 0);

  const remove = (id) => {
    setCart(cart.filter((i) => i.id !== id));
  };

  const checkout = async () => {
    if (!user) return alert("Login required");

    const res = await fetch(`${API}/api/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`,
      },
      body: JSON.stringify({ items: cart }),
    });

    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  return (
    <div className="cart-overlay">
      <div className="cart">
        <h2>Your Cart</h2>

        {cart.length === 0 && <p>No items added</p>}

        {cart.map((i) => (
          <div className="cart-item" key={i.id}>
            <span>{i.name}</span>
            <span>${i.price}</span>
            <button onClick={() => remove(i.id)}>✕</button>
          </div>
        ))}

        <h3>Total: ${total}</h3>

        <button className="checkout-btn" onClick={checkout}>
          Checkout
        </button>
        <button className="close-btn" onClick={close}>
          Close
        </button>
      </div>
    </div>
  );
}

/* ---------------- LOGIN ---------------- */
function Login({ setUser }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");

  const submit = async () => {
    const r = await fetch(`${API}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (r.status !== 200) return alert("Invalid credentials");

    const d = await r.json();
    localStorage.setItem("user", JSON.stringify(d));
    setUser(d);
    nav("/");
  };

  return (
    <div className="auth">
      <input placeholder="Email" onChange={(e) => setEmail(e.target.value)} />
      <input type="password" placeholder="Password" onChange={(e) => setPass(e.target.value)} />
      <button onClick={submit}>Login</button>

      <p>
        Don't have an account? <Link to="/signup">Sign up</Link>
      </p>
    </div>
  );
}

/* ---------------- SIGNUP ---------------- */
function Signup() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");

  const submit = async () => {
    const r = await fetch(`${API}/api/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (r.status !== 200) return alert("Email already exists");

    alert("Account created! Please login.");
    nav("/login");
  };

  return (
    <div className="auth">
      <input placeholder="Email" onChange={(e) => setEmail(e.target.value)} />
      <input type="password" placeholder="Password" onChange={(e) => setPass(e.target.value)} />
      <button onClick={submit}>Sign Up</button>
    </div>
  );
}

/* ---------------- DASHBOARD ---------------- */
function Dashboard({ user }) {
  const [myDeals, setMyDeals] = useState([]);

  useEffect(() => {
    if (!user) return;

    fetch(`${API}/api/my-deals`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((r) => {
        if (r.status === 401) {
          alert("Session expired. Login again.");
          localStorage.removeItem("user");
          window.location.reload();
        }
        return r.json();
      })
      .then(setMyDeals);
  }, [user]);

  return (
    <div className="dashboard">
      <h2>Your Licenses</h2>
      <div className="grid">
        {myDeals.map((d, i) => (
          <div className="card" key={i}>
            <b>{d.deal_name}</b>
            <p>{d.license_key}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
